/**
 * Enhanced Dialog Accessibility System
 * 
 * This module provides comprehensive dialog accessibility support including:
 * - Background content hiding for screen readers
 * - Focus management integration
 * - WCAG 2.1.1 (Keyboard) and 2.4.3 (Focus Order) compliance
 * 
 * Addresses accessibility issues:
 * - KDAP2-2935: Focus should move only within dialog
 * - KDAP2-2936: Focus should follow logical sequence
 */

export function setupDialogBackgroundHiding(rootId = 'root') {
    const getRoot = () => document.getElementById(rootId);

    const isDialogVisible = (dialog: Element) => {
        if (!(dialog instanceof HTMLElement)) {
            return false;
        }
        if (dialog.closest('[hidden]')) {
            return false;
        }
        const style = window.getComputedStyle(dialog);
        return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
    };
    const isInteractiveDialog = (dialog: Element): boolean => {
        if (!(dialog instanceof HTMLElement)) {
            return false;
        }
        const interactiveSelector =
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
        return dialog.querySelector(interactiveSelector) !== null;
    };

    const updateAriaHidden = () => {
        const rootElement = getRoot();
        if (!rootElement) {
            return;
        }

        const activeDialogs = Array.from(
            document.querySelectorAll('[role="dialog"][aria-modal="true"], [role="alertdialog"][aria-modal="true"]'),
        );

        const visibleDialogs = activeDialogs.filter(isDialogVisible);
        const hasVisibleDialog = visibleDialogs.length > 0;
        const hasInteractiveDialog = visibleDialogs.some(isInteractiveDialog);
        const currentHidden = rootElement.getAttribute('aria-hidden');
        const currentInert = rootElement.hasAttribute('inert');

        if (hasVisibleDialog && currentHidden !== 'true') {
            rootElement.setAttribute('aria-hidden', 'true');

            if (hasInteractiveDialog && !currentInert) {
                rootElement.setAttribute('inert', '');
            }

            // Announce dialog opening to screen readers
            announceDialogToScreenReaders(visibleDialogs);
        } else if (hasVisibleDialog && !hasInteractiveDialog && currentInert) {
            rootElement.removeAttribute('inert');
        } else if (!hasVisibleDialog && (currentHidden !== null || currentInert)) {
            rootElement.removeAttribute('aria-hidden');
            rootElement.removeAttribute('inert');
        }
    };

    const announceDialogToScreenReaders = (dialogs: Element[]) => {
        // Find visible dialogs and announce their presence
        dialogs.forEach(dialog => {
            if (isDialogVisible(dialog)) {
                const labelledBy = dialog.getAttribute('aria-labelledby');
                const describedBy = dialog.getAttribute('aria-describedby');

                if (labelledBy) {
                    const labelElement = document.getElementById(labelledBy);
                    if (labelElement) {
                        // Create or update live region for announcements
                        let liveRegion = document.getElementById('dialog-announcer');
                        if (!liveRegion) {
                            liveRegion = document.createElement('div');
                            liveRegion.id = 'dialog-announcer';
                            liveRegion.setAttribute('aria-live', 'polite');
                            liveRegion.setAttribute('aria-atomic', 'true');
                            liveRegion.style.position = 'absolute';
                            liveRegion.style.left = '-9999px';
                            liveRegion.style.width = '1px';
                            liveRegion.style.height = '1px';
                            liveRegion.style.overflow = 'hidden';
                            document.body.appendChild(liveRegion);
                        }

                        // Announce dialog opening
                        setTimeout(() => {
                            if (liveRegion) {
                                liveRegion.textContent = `Dialog opened: ${labelElement.textContent}`;
                            }
                        }, 100);
                    }
                }
            }
        });
    };

    const observer = new MutationObserver(updateAriaHidden);
    observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['role', 'aria-modal', 'hidden'],
    });

    if (document.readyState !== 'loading') {
        updateAriaHidden();
    } else {
        document.addEventListener('DOMContentLoaded', updateAriaHidden, { once: true });
    }

    return observer;
}

/**
 * Initialize comprehensive dialog accessibility system
 * This should be called once when the application starts
 */
export function initializeDialogAccessibility() {
    // Setup background hiding
    const observer = setupDialogBackgroundHiding();

    // Add global keyboard handling for dialogs
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            const activeDialogs = Array.from(
                document.querySelectorAll('[role="dialog"][aria-modal="true"], [role="alertdialog"][aria-modal="true"]'),
            ) as HTMLElement[];

            // Find all visible dialogs
            const visibleDialogs = activeDialogs.filter(dialog => {
                const style = window.getComputedStyle(dialog);
                return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
            });

            if (visibleDialogs.length === 0) return;

            // Find the topmost dialog by z-index (highest z-index = on top)
            const topmostDialog = visibleDialogs.reduce((topDialog, dialog) => {
                const topZIndex = parseInt(window.getComputedStyle(topDialog).zIndex || '0', 10) || 0;
                const dialogZIndex = parseInt(window.getComputedStyle(dialog).zIndex || '0', 10) || 0;
                return dialogZIndex > topZIndex ? dialog : topDialog;
            });

            if (topmostDialog) {
                // Look for close button - use exact match or common close patterns
                // Avoid matching buttons where "close" is part of unrelated text
                const closeButton = topmostDialog.querySelector(
                    '[aria-label="close" i], ' +
                    '[aria-label="Close modal" i], ' +
                    '[aria-label="Close dialog" i], ' +
                    '[aria-label="Close Demat mode dialog" i], ' +
                    '.close-button, [data-testid="close-dialog"]'
                );
                if (closeButton && closeButton instanceof HTMLElement) {
                    closeButton.click();
                }
            }
        }
    }, true);

    return observer;
}

/**
 * Utility function to check if any dialog is currently open
 */
export function hasOpenDialog(): boolean {
    const activeDialogs = Array.from(
        document.querySelectorAll('[role="dialog"][aria-modal="true"], [role="alertdialog"][aria-modal="true"]'),
    );

    return activeDialogs.some(dialog => {
        if (!(dialog instanceof HTMLElement)) {
            return false;
        }
        const style = window.getComputedStyle(dialog);
        return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
    });
}

/**
 * Utility function to get the currently active dialog
 */
export function getActiveDialog(): HTMLElement | null {
    const activeDialogs = Array.from(
        document.querySelectorAll('[role="dialog"][aria-modal="true"], [role="alertdialog"][aria-modal="true"]'),
    ) as HTMLElement[];

    // Return the first visible dialog (should be the topmost one)
    return activeDialogs.find(dialog => {
        const style = window.getComputedStyle(dialog);
        return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
    }) || null;
}
